import * as React from "react"
import { format, parse } from "date-fns"
import { Calendar as CalendarIcon, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

// Get date format based on region
function getDateFormat(region = 'AU') {
  // US uses MM-DD-YYYY, others use DD-MM-YYYY
  return region === 'US' ? 'MM-dd-yyyy' : 'dd-MM-yyyy';
}

/**
 * DatePicker component using shadcn Calendar
 * 
 * @param {Object} props
 * @param {string} props.value - Date string in YYYY-MM-DD format (or empty)
 * @param {function} props.onChange - Callback with date string in YYYY-MM-DD format
 * @param {string} props.placeholder - Placeholder text when no date selected
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the picker is disabled
 * @param {boolean} props.clearable - Whether to show a clear button (default: true)
 * @param {boolean} props.showIcon - Whether to show the calendar icon (default: true)
 * @param {string} props.region - Region code for date formatting (AU, US, UK, EU)
 */
export function DatePicker({ 
  value, 
  onChange, 
  placeholder = "Select date",
  className,
  disabled = false,
  clearable = true,
  showIcon = true,
  region = 'AU',
  ...props 
}) {
  const [open, setOpen] = React.useState(false)

  // Parse the value string to a Date object
  const dateValue = React.useMemo(() => {
    if (!value) return undefined
    try {
      return parse(value, "yyyy-MM-dd", new Date())
    } catch {
      return undefined
    }
  }, [value])

  // Handle date selection
  const handleSelect = (date) => {
    if (date) {
      onChange(format(date, "yyyy-MM-dd"))
    }
    setOpen(false)
  }

  // Handle clear - prevent event bubbling completely
  const handleClear = (e) => {
    e.preventDefault()
    e.stopPropagation()
    onChange("")
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          type="button"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal h-9 px-2 sm:px-3 overflow-hidden",
            !dateValue && "text-muted-foreground",
            className
          )}
          {...props}
        >
          {showIcon && <CalendarIcon className="h-4 w-4 shrink-0 mr-1 sm:mr-2" />}
          {dateValue ? (
            <span className="flex-1 truncate text-xs sm:text-sm">
              {format(dateValue, getDateFormat(region))}
            </span>
          ) : (
            <span className="flex-1 truncate text-xs sm:text-sm">{placeholder}</span>
          )}
          {clearable && dateValue && (
            <span 
              role="button"
              tabIndex={0}
              className="h-4 w-4 shrink-0 ml-1 opacity-50 hover:opacity-100 cursor-pointer inline-flex items-center justify-center rounded-sm hover:bg-muted"
              onClick={handleClear}
              onKeyDown={(e) => e.key === 'Enter' && handleClear(e)}
            >
              <X className="h-3 w-3" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-auto p-0" 
        align="start"
        style={{ zIndex: 9999 }}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={handleSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}
